import { zodResolver } from '@hookform/resolvers/zod';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import FileIcon from '@mui/icons-material/InsertDriveFile';
import Box from '@mui/material/Box';
import { grey } from '@mui/material/colors';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { FC, useEffect, useRef } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import {
  ActionBtnStyled,
  AttachmentErrorStyled,
  AttachmentFormInputStyled,
  AttachmentNameStyled,
  AttachmentPhotoWrapStyled,
  AttachmentWrapStyled,
  FlexContainerControlsStyled,
  FlexContainerMessageStyled,
  FormElementWrapStyled,
  FormStyled,
  IconWrapStyled,
  TypographyTitleStyled,
  getPopupContentStyle,
} from './AddAttachmentPopup.styled';
import { TicketAttachmentFormValuesT } from './declarations';
import useAddAttachmentPopup from './hooks/useAddAttachmentPopup';
import validationSchema from './validationSchema';
import ErrorSection from 'components/ErrorSection/ErrorSection';
import { FormInput } from 'components/Form';
import Popup from 'components/Popup/Popup';
import SuccessSection from 'components/SuccessSection/SuccessSection';
import { useResponsive } from 'contexts/ResponsiveContext';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import { NoopT } from 'declarations/types/common';
import { AttachFileIcon, PhotoCameraIcon } from 'icons';

export type AddAttachmentPopupPropsT = {
  ticketId: string;
  categoryName: string;
  isOpen: boolean;
  assignToWarrCloud: boolean;
  onClose: NoopT;
};

const AddAttachmentPopup: FC<AddAttachmentPopupPropsT> = ({
  ticketId,
  categoryName,
  isOpen,
  assignToWarrCloud,
  onClose,
}) => {
  /**
   * Hooks
   */
  const fileInput: any = useRef();
  const { isMobile } = useResponsive();
  const {
    handleSubmitForm,
    isCreatingAttachment,
    isAttachmentCreationError,
    createAttachmentErrorMessage,
    createAttachmentValidationErrorMessage,
    isAttachmentSuccessfullyPublished,
    showAttachmentsForm,
    handleClosePopup,
  } = useAddAttachmentPopup({ ticketId, categoryName, assignToWarrCloud });
  const fromMethods = useForm<TicketAttachmentFormValuesT>({
    defaultValues: {
      note: '',
    },
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
  });

  /**
   * Variables
   */
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = fromMethods;
  const attachmentFileValue = useWatch<TicketAttachmentFormValuesT, 'attachmentFile'>({
    control,
    name: 'attachmentFile',
  });
  const attachmentPhotoValue = useWatch<TicketAttachmentFormValuesT, 'attachmentPhoto'>({
    control,
    name: 'attachmentPhoto',
  });
  const attachmentFileNames = attachmentFileValue
    ? Array.from(attachmentFileValue)
        .map(file => file.name)
        .join(', ')
    : '';
  const attachmentPhotoNames = attachmentPhotoValue
    ? Array.from(attachmentPhotoValue)
        .map(file => file.name)
        .join(', ')
    : '';
  const attachmentNames = [attachmentFileNames, attachmentPhotoNames]
    .filter(name => name !== '')
    .join(',');

  const attachmentValues = [
    ...Array.from(attachmentFileValue || []),
    ...Array.from(attachmentPhotoValue || []),
  ].filter(attachment => attachment);

  /**
   * Handlers
   */
  const handleDropZoneClick = () => {
    if (fileInput.current) {
      fileInput.current.click();
    }
  };

  const handleDragOver = (event: any) => {
    event.preventDefault();
  };

  const handleDrop = (event: any) => {
    event.preventDefault();
    const files = event?.dataTransfer?.files;
    if (files.length > 0) {
      const attachments = files;
      fromMethods.setValue(`attachmentFile`, attachments);
    }
  };

  const handleFileChange = (event: any) => {
    const file = event.target.files;
    if (file) {
      fromMethods.setValue(`attachmentFile`, file);
    }
  };

  /**
   * Effects
   */
  useEffect(
    () => () => {
      if (isOpen) {
        reset();
      }
    },
    [isOpen, reset]
  );

  return (
    <Popup
      nested
      popupId={ScrollLockIdE.AddAttachmentPopup}
      contentStyle={getPopupContentStyle(isMobile)}
      isOpen={isOpen}
      onClose={handleClosePopup(onClose)}
      showCloseBtn={isAttachmentSuccessfullyPublished || isAttachmentCreationError}
      closeOnEscape={false}
    >
      {showAttachmentsForm && !isAttachmentCreationError && (
        <>
          <TypographyTitleStyled variant="h2" color="dark" align="center">
            Publish Attachment
          </TypographyTitleStyled>
          <FormProvider {...fromMethods}>
            <FormStyled role="form" onSubmit={handleSubmit(handleSubmitForm)}>
              {!isMobile && (
                <Box px={4} pt={0} pb={6}>
                  <Stack
                    onClick={handleDropZoneClick}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    direction="column"
                    justifyContent="center"
                    spacing={4}
                    sx={{
                      marginTop: attachmentValues.length > 0 ? '30px' : '10px',
                      height: '300px',
                      borderWidth: '1px',
                      borderStyle: 'dashed',
                      borderColor: grey['500'],
                      borderRadius: 2,
                      p: '10px',
                      cursor: 'pointer',
                    }}
                  >
                    {attachmentValues.length <= 0 && (
                      <Box id="upload-container" textAlign="center">
                        <CloudUploadOutlinedIcon
                          sx={{
                            fontSize: '50px',
                            ':hover': {
                              color: grey['900'],
                              cursor: 'pointer',
                            },
                          }}
                        />
                        <Stack direction="column" justifyContent="center" alignItems="center">
                          <Typography>Select a file or drag and drop here</Typography>
                          <Typography fontSize={12} color={grey['500']}>
                            Size no more than 10MB
                          </Typography>
                        </Stack>
                        <input
                          style={{ display: 'none' }}
                          type="file"
                          name="attachmentFile"
                          placeholder="Attachment File"
                          multiple
                          ref={fileInput}
                          onChange={handleFileChange}
                        />
                      </Box>
                    )}

                    {attachmentValues.length > 0 && (
                      <Box>
                        <List dense sx={{ maxHeight: '270px', overflowY: 'auto' }}>
                          {attachmentValues.map(file => (
                            <ListItem>
                              <FileIcon />
                              <ListItemText
                                primary={file.name}
                                sx={{
                                  marginLeft: '10px',
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </Stack>
                </Box>
              )}
              <FormElementWrapStyled>
                <FormInput
                  name="note"
                  label="Enter Attachment Note"
                  placeholder="Attachment Note"
                />
              </FormElementWrapStyled>

              <AttachmentWrapStyled justify="flex-start" align="start">
                {isMobile && (
                  <>
                    <AttachmentWrapStyled justify="flex-start" align="start">
                      <AttachmentFormInputStyled
                        name="attachmentFile"
                        placeholder="Attachment File"
                        type="file"
                        multiple
                        label={
                          <IconWrapStyled justify="center" title="Choose File">
                            <AttachFileIcon
                              size={isMobile ? '3xl' : '2xl'}
                              color={attachmentFileNames ? 'green' : 'darkBlue'}
                            />
                          </IconWrapStyled>
                        }
                      />
                    </AttachmentWrapStyled>
                    <AttachmentPhotoWrapStyled>
                      <AttachmentFormInputStyled
                        name="attachmentPhoto"
                        placeholder="Attachment Photo"
                        type="file"
                        capture="user"
                        accept="image/*"
                        multiple
                        label={
                          <IconWrapStyled justify="center">
                            <PhotoCameraIcon
                              size={isMobile ? '3xl' : '2xl'}
                              color={attachmentPhotoNames ? 'green' : 'darkBlue'}
                            />
                          </IconWrapStyled>
                        }
                      />
                    </AttachmentPhotoWrapStyled>
                    <AttachmentNameStyled size="md">
                      {attachmentNames || 'Attachment not selected'}
                    </AttachmentNameStyled>
                  </>
                )}
              </AttachmentWrapStyled>
              {!isValid && (
                <AttachmentErrorStyled size={isMobile ? 'md' : 'sm'} color="error">
                  {errors.attachmentFile?.message || errors.attachmentPhoto?.message}
                </AttachmentErrorStyled>
              )}
              <FlexContainerControlsStyled justify={isMobile ? 'center' : 'flex-end'}>
                <ActionBtnStyled
                  type="button"
                  variant="outline"
                  size="xl"
                  onClick={handleClosePopup(onClose)}
                >
                  Cancel
                </ActionBtnStyled>
                <ActionBtnStyled size="xl" isLoading={isCreatingAttachment}>
                  Publish
                </ActionBtnStyled>
              </FlexContainerControlsStyled>
            </FormStyled>
          </FormProvider>
        </>
      )}

      {isAttachmentSuccessfullyPublished && (
        <FlexContainerMessageStyled direction="column">
          <SuccessSection message="Attachment successfully published" />
        </FlexContainerMessageStyled>
      )}

      {isAttachmentCreationError && (
        <FlexContainerMessageStyled direction="column">
          <ErrorSection
            errorDetails={createAttachmentErrorMessage || createAttachmentValidationErrorMessage}
          />
        </FlexContainerMessageStyled>
      )}
    </Popup>
  );
};

export default AddAttachmentPopup;
